import * as React from 'react';
import {useRef, useState} from "react";
import {Card, CardContent, Button} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import PublicIcon from '@mui/icons-material/Public';
import countryCodes, {CountryProperty} from 'country-codes-list';
import HandymanIcon from '@mui/icons-material/Handyman';

import {
    FilterList,
    FilterListItem,
    FilterLiveSearch,
    useListContext,
    useTranslate
} from 'react-admin';
import {useListFilterContext} from "ra-core";

export const countries = ['DE', 'FR', 'BE', 'NL', 'IT', 'SE', 'SI', 'LU', 'HU', 'AT', 'CH', 'CA', 'GB', 'IR', 'CZ', 'AU', 'GR', 'RO', 'PT', 'EE', 'ES', 'PL', 'LV', 'DK', 'FI', 'TR', 'NO', 'US'];

const DealersListFilters = () => {
    const t = useTranslate();
    const minStockInputRef = useRef<{ value: string | null }>({value: null});
    const maxStockInputRef = useRef<{ value: string | null }>({value: null});

    const {filterValues, setFilters} = useListFilterContext();
    const {data, isLoading, isFetching} = useListContext();

    const emptyStockFilter = {
        "stock[min]": filterValues['stock[min]'] ?? '',
        "stock[max]": filterValues['stock[max]'] ?? '',
    }

    const [stockFilter, setStockFilter] = useState(emptyStockFilter);
    const [backdrop, setBackdrop] = useState(false);

    const isSelected = (value, filters) => {
        const tags = filters.tags || [];

        return tags.includes(value.tag);
    };

    const toggleFilter = (value, filters) => {
        const tags = filters.tags || [];

        return {
            ...filters,
            tags: tags.includes(value.tag)
                // Remove the category if it was already present
                ? tags.filter(v => v !== value.tag)
                // Add the category if it wasn't already present
                : [...tags, value.tag]
        };
    };

    const handleStockChange = (event) => {
        const stock = parseInt(event.target.value, 10);
        const name = `stock[${event.target.name}]`;

        const filters = {...filterValues};

        if (isNaN(stock) && name in filters) {
            delete filters[name];
        } else if (!isNaN(stock)) {
            filters[name] = stock;
        }

        setFilters({...filters}, null);
    };

    const hide = (key: string): boolean => {
        if (filterValues.hasOwnProperty(key)) {
            return false;
        }
        return data && data.length === 0;
    }

    interface Filter {
        key: string;
        name: string;
        component: JSX.Element;
        render?: (value: any, key?: string) => string;
    }

    const countryElement = (code: string) => {
        let returnedString: string = code;

        const country = countryCodes.findOne('countryCode' as CountryProperty, code);

        if (country) {
            returnedString = `${country.flag} ${country.countryNameEn}`;

        }

        return <>
            {returnedString}
        </>;
    }

    const filters: Filter[] = [
        {
            key: 'q',
            name: 'resources.dealers.filters.search_phrase',
            component: (
                <FilterLiveSearch key="filter_search" disabled={hide('q')}/>
            ),
        },

        {
            key: 'isActive',
            name: 'resources.dealers.list.isActive',
            component: (
                <FilterList
                    label="resources.dealers.list.isActive"
                    icon={<ToggleOnIcon/>}
                    key="filter_active"
                    display={hide('isActive') ? 'none' : 'block'}
                >
                    <FilterListItem
                        label="ra.boolean.true"
                        value={{
                            isActive: true
                        }}
                    />
                    <FilterListItem
                        label="ra.boolean.false"
                        value={{
                            isActive: false
                        }}
                    />
                </FilterList>
            ),
            render: (value) => {
                return value ? t('ra.boolean.true') : t('ra.boolean.false');
            }
        },
        {
            key: 'isServicePartner',
            name: 'resources.dealers.list.isServicePartner',
            component: (
                <FilterList
                    label="resources.dealers.list.isServicePartner"
                    icon={<HandymanIcon/>}
                    key="filter_servicePartner"
                    display={hide('isServicePartner') ? 'none' : 'block'}
                >
                    <FilterListItem
                        label="ra.boolean.true"
                        value={{
                            isServicePartner: true
                        }}
                    />
                    <FilterListItem
                        label="ra.boolean.false"
                        value={{
                            isServicePartner: false
                        }}
                    />
                </FilterList>
            ),
            render: (value) => {
                return value ? t('ra.boolean.true') : t('ra.boolean.false');
            }
        },
        {
            key: 'country',
            name: 'resources.dealers.list.country',
            component: (
                <FilterList
                    label="resources.dealers.list.country"
                    icon={<PublicIcon/>}
                >
                    {countries.map((code, key) => (
                        <FilterListItem
                            key={key}
                            label={countryElement(code)}
                            value={{country: code}}
                        />
                    ))}
                </FilterList>
            )
        },


        {
            key: 'archived',
            name: 'resources.dealers.filters.included_archived',
            component: (
                <FilterList
                    label="resources.dealers.filters.included_archived"
                    icon={<ArchiveIcon/>} sx={{color: '#BA0001'}}
                    key="filter_archived"
                    display={hide('archivedIncluded') ? 'none' : 'block'}
                >
                    <FilterListItem
                        label="ra.boolean.true"
                        value={{
                            archivedIncluded: true
                        }}
                    />
                </FilterList>
            ),
        }
    ];

    return (
        <Card
            sx={{
                display: {
                    xs: 'none',
                    md: 'block',
                },
                order: -1,
                flex: '0 0 15em',
                mr: 2,
                mt: 6,
                alignSelf: 'flex-start',
            }}
        >
            <CardContent sx={{pt: 1, position: 'relative'}}>
                <Backdrop
                    sx={{color: '#fff', position: 'absolute', zIndex: (theme) => 9}}
                    open={isFetching || isLoading || backdrop}
                    onClick={() => setBackdrop(false)}
                >
                    <CircularProgress color="inherit"/>
                </Backdrop>
                {
                    Object.keys(filterValues).length > 0 && (
                        <Tooltip
                            title={
                                <TableContainer component={Paper}>
                                    <Table size="small" aria-label="table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Filter</TableCell>
                                                <TableCell align="right">Value</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.keys(filterValues).map((key) => {
                                                const k = key.startsWith("stock[") ? "stock" : key;
                                                const filter = filters.find(f => f.key === k);
                                                return (
                                                    <TableRow
                                                        key={key}
                                                        sx={{'&:last-child td, &:last-child th': {border: 0}}}
                                                    >
                                                        <TableCell component="th" scope="row">
                                                            {t((filter?.name as string))}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {
                                                                filter?.render ? filter['render'](filterValues[key], key) : filterValues[key]
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            }>
                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                color={'primary'}
                                onClick={() => {
                                    setFilters({}, undefined);
                                    setStockFilter(emptyStockFilter);

                                    minStockInputRef.current.value = null;
                                    maxStockInputRef.current.value = null;

                                    setTimeout(() => {
                                        (document.getElementById('q') as HTMLInputElement).value = '';
                                        setFilters({}, undefined);
                                    }, 1000);

                                }}
                                startIcon={<RestartAltIcon/>}
                            >
                                {t('resources.dealers.filters.reset_filters')}
                            </Button>
                        </Tooltip>
                    )
                }
                {filters.map((f, key) => (f.component))}
            </CardContent>
        </Card>
    );
};

export default DealersListFilters;
